import { useFriendActions } from '@/features/friends/hooks/use-friend-actions';
import { useFriendshipStatus } from '@/features/friends/hooks/use-friendship-status';
import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { ActivityIndicator, Pressable, Text, View } from 'react-native';
import { useAppTheme } from '@/providers/theme-provider';

interface ProfileActionsProps {
  isCurrentUser?: boolean;
  userId: string;
  onEditProfile?: () => void;
}

export function ProfileActions({
  isCurrentUser = false,
  userId,
  onEditProfile
}: ProfileActionsProps) {
  const theme = useAppTheme();
  const { data: friendshipStatus, isLoading: isLoadingStatus } = useFriendshipStatus(userId, {
    enabled: !isCurrentUser,
  });
  const { sendRequest, acceptRequest, rejectRequest } = useFriendActions();
  const isSendingRequest = sendRequest.isPending;
  const isAccepting = acceptRequest.isPending;
  const isRejecting = rejectRequest.isPending;

  const handleAddFriend = () => {
    sendRequest.mutate(userId);
  };

  if (isCurrentUser) {
    return null;
  }

  if (isLoadingStatus) {
    return (
      <View className="flex-row items-center justify-center gap-2 p-4 rounded-lg mb-4">
        <ActivityIndicator size="small" color={theme.colors.primary} />
        <Text className="font-medium text-muted-foreground">
          Loading...
        </Text>
      </View>
    );
  }

  const status = friendshipStatus?.status;
  const isPendingSent = status === 'pending_sent';
  const isPendingReceived = status === 'pending_received';
  const isFriends = status === 'friends';

  if (isFriends) {
    return null;
  }

  if (isPendingSent) {
    return (
      <View
        className="flex-row items-center justify-center gap-2 p-4 rounded-lg mb-4 bg-surface-secondary border border-border"
      >
        <Ionicons name="time-outline" size={20} className="text-muted-foreground" />
        <Text className="font-medium text-muted-foreground">
          Friend Request Sent
        </Text>
      </View>
    );
  }

  if (isPendingReceived && friendshipStatus?.requestId) {
    const isProcessing = isAccepting || isRejecting;
    return (
      <View className="flex-row gap-2 mb-4">
        <Pressable
          onPress={() => acceptRequest.mutate(friendshipStatus.requestId!)}
          disabled={isProcessing}
          className="flex-1 flex-row items-center justify-center gap-2 p-4 rounded-lg bg-primary"
          style={{ opacity: isProcessing ? 0.6 : 1 }}
          accessibilityRole="button"
          accessibilityLabel={isAccepting ? 'Accepting friend request' : 'Accept friend request'}
        >
          {isAccepting ? (
            <ActivityIndicator size="small" className="text-dim" />
          ) : (
            <Ionicons name="checkmark" size={20} />
          )}
          <Text className="font-medium">
            {isAccepting ? 'Accepting...' : 'Accept Request'}
          </Text>
        </Pressable>
        <Pressable
          onPress={() => rejectRequest.mutate(friendshipStatus.requestId!)}
          disabled={isProcessing}
          className="flex-1 flex-row items-center justify-center gap-2 p-4 rounded-lg bg-surface-secondary border border-border"
          style={{ opacity: isProcessing ? 0.6 : 1 }}
          accessibilityRole="button"
          accessibilityLabel={isRejecting ? 'Ignoring friend request' : 'Ignore friend request'}
        >
          {isRejecting ? (
            <ActivityIndicator size="small" className="text-muted-foreground" />
          ) : (
            <Ionicons name="close" size={20} className="text-muted-foreground" />
          )}
          <Text className="font-medium text-muted-foreground">
            {isRejecting ? 'Ignoring...' : 'Ignore'}
          </Text>
        </Pressable>
      </View>
    );
  }

  return (
    <Pressable
      onPress={handleAddFriend}
      disabled={isSendingRequest}
      className="flex-row items-center justify-center gap-2 p-4 rounded-lg mb-4 bg-primary"
      style={{
        opacity: isSendingRequest ? 0.6 : 1,
      }}
      accessibilityRole="button"
      accessibilityLabel={isSendingRequest ? 'Sending friend request' : 'Add friend'}
    >
      {isSendingRequest ? (
        <ActivityIndicator size="small" className="text-dim" />
      ) : (
        <Ionicons name="person-add" size={20} />
      )}
      <Text className="font-medium">
        {isSendingRequest ? 'Sending...' : 'Add Friend'}
      </Text>
    </Pressable>
  );
}
